import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { deviceAPI } from '@/services/api';
import type { Device } from '@/types/domain';

export interface UseDevicesReturn {
  devices: Device[];
  loading: boolean;
  error: string | null;
  success: string | null;
  refresh: () => Promise<void>;
  lock: (id: number) => Promise<void>;
  unlock: (id: number) => Promise<void>;
  deleteDevice: (id: number) => Promise<void>;
  clearMessages: () => void;
}

/**
 * 设备数据管理 Hook
 * 封装设备列表加载、锁定/解锁/删除等操作
 */
export function useDevices(): UseDevicesReturn {
  const { t } = useTranslation();
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  /**
   * 加载设备列表
   */
  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const response = await deviceAPI.getAll();
      setDevices(response.data || []);
      setError(null);
    } catch (err: unknown) {
      console.error('Failed to load devices:', err);
      const axiosError = err as { response?: { status?: number; data?: { error?: string } } };
      if (axiosError.response?.status === 401) {
        setError(t('devices.sessionExpired'));
      } else {
        setError(axiosError.response?.data?.error || t('devices.loadFailed'));
      }
      setDevices([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  /**
   * 锁定设备
   */
  const lock = useCallback(
    async (id: number) => {
      try {
        await deviceAPI.lock(id);
        setSuccess(t('devices.lockSuccess'));
        refresh();
      } catch (err: unknown) {
        const axiosError = err as { response?: { data?: { error?: string } } };
        setError(axiosError.response?.data?.error || t('devices.lockFailed'));
      }
    },
    [t, refresh]
  );

  /**
   * 解锁设备
   */
  const unlock = useCallback(
    async (id: number) => {
      try {
        await deviceAPI.unlock(id);
        setSuccess(t('devices.unlockSuccess'));
        refresh();
      } catch (err: unknown) {
        const axiosError = err as { response?: { data?: { error?: string } } };
        setError(axiosError.response?.data?.error || t('devices.unlockFailed'));
      }
    },
    [t, refresh]
  );

  /**
   * 删除（注销）设备
   */
  const deleteDevice = useCallback(
    async (id: number) => {
      if (!window.confirm(t('devices.confirmDeactivate'))) {
        return;
      }

      try {
        await deviceAPI.delete(id);
        setSuccess(t('devices.deactivatedSuccess'));
        refresh();
      } catch (err: unknown) {
        const axiosError = err as { response?: { data?: { error?: string } } };
        setError(axiosError.response?.data?.error || t('devices.deactivateFailed'));
      }
    },
    [t, refresh]
  );

  /**
   * 清除错误和成功消息
   */
  const clearMessages = useCallback(() => {
    setError(null);
    setSuccess(null);
  }, []);

  // 初始化时加载设备列表
  useEffect(() => {
    refresh();
  }, [refresh]);

  return {
    devices,
    loading,
    error,
    success,
    refresh,
    lock,
    unlock,
    deleteDevice,
    clearMessages,
  };
}
